import React, { useState } from "react";
import { Link } from "@tanstack/react-router";
import ReactiveStackLogo from "./Logo";
import ReactiveStackStaticLogo from "./StaticLogo";
import { UpworkTopRatedPlus } from "./UpworkTopRatedPlus";
import { COMPANY_INFO, CONTACT_INFO } from "../constants/company";

const UnderConstructionPage: React.FC = () => {
  const [useStaticLogo, setUseStaticLogo] = useState<boolean>(false);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black flex items-center justify-center p-4 relative overflow-hidden">
      {/* Main Content */}
      <div className="construction-card p-8 md:p-12 max-w-2xl mx-auto text-center relative z-20">
        {/* Company Logo */}
        <div className="mb-8 relative">
          {useStaticLogo ? (
            <ReactiveStackStaticLogo size={120} className="mx-auto" />
          ) : (
            <ReactiveStackLogo size={120} animated={true} className="mx-auto" />
          )}

          {/* Logo Toggle Button */}
          <button
            onClick={() => setUseStaticLogo(!useStaticLogo)}
            className="absolute top-0 right-0 transform translate-x-8 -translate-y-2 p-2 text-white/50 hover:text-white/80 transition-colors duration-300"
            aria-label={
              useStaticLogo
                ? "Switch to animated logo"
                : "Switch to static logo"
            }
            title={
              useStaticLogo
                ? "Switch to animated logo"
                : "Switch to static logo"
            }
          >
            {useStaticLogo ? (
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z"
                  clipRule="evenodd"
                />
              </svg>
            ) : (
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zM7 8a1 1 0 012 0v4a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v4a1 1 0 102 0V8a1 1 0 00-1-1z"
                  clipRule="evenodd"
                />
              </svg>
            )}
          </button>
        </div>

        {/* Main Heading - White + Neon Style */}
        <h1 className="text-4xl md:text-6xl font-bold mb-6">
          <span className="inline-block synchronized-glow">
            <span className="text-reactive-white">Reactive</span>
            <span className="text-stack-neon">Stack</span>
          </span>
        </h1>

        {/* Company Tagline */}
        <p className="text-lg md:text-xl text-blue-300 mb-4 font-medium">
          {COMPANY_INFO.tagline}
        </p>

        {/* Upwork Top Rated Plus Badge */}
        <div className="mb-6 flex justify-center">
          <a
            href={CONTACT_INFO.social.upwork}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-green-600/20 to-green-500/30 border border-green-500/40 rounded-full text-sm font-medium text-green-300 hover:text-green-200 hover:border-green-400/60 transition-all duration-300 hover:scale-105 shadow-lg hover:shadow-green-500/20"
          >
            <div className="flex flex-row gap-1">
              <UpworkTopRatedPlus />
              <span className="text-base font-semibold">Top Rated Plus</span>
            </div>

            <span className="text-white text-base">at</span>

            {/* Upwork Logo */}
            <div className="flex flex-row gap-1">
              <img src={"/upwork-green.png"} alt="Upwork" className="w-6 h-6" />
              <span className="text-base font-semibold">Upwork</span>
            </div>
          </a>
        </div>

        {/* Subheading */}
        <h2 className="text-xl md:text-2xl text-white/90 mb-6 font-light">
          Coming Soon
        </h2>

        {/* Company Description */}
        <p className="text-lg text-white/80 mb-6 leading-relaxed max-w-3xl mx-auto">
          {COMPANY_INFO.description.short}
        </p>

        {/* Technology Stack */}
        <div className="mb-8 p-4 bg-white/5 rounded-xl border border-green-400/20 shadow-lg shadow-green-400/10">
          <p className="text-white text-sm mb-3 font-medium">Our Tech Stack</p>
          <div className="flex flex-wrap justify-center gap-2">
            {[...COMPANY_INFO.technologies.frontend].map((tech) => (
              <span
                key={tech}
                className="px-3 py-1 text-xs bg-gradient-to-r from-white/10 to-green-400/20 text-green-200 rounded-full border border-green-400/40 hover:border-green-300/60 transition-all duration-300 hover:shadow-sm hover:shadow-green-400/20"
              >
                {tech}
              </span>
            ))}
          </div>
        </div>

        {/* Progress Bar */}
        {/* <div className="mb-8">
          <div className="flex justify-between text-sm text-white font-medium mb-2">
            <span>Progress</span>
            <span>75%</span>
          </div>
          <div className="w-full bg-white/20 rounded-full h-3 overflow-hidden shadow-inner">
            <div
              className="h-full bg-gradient-to-r from-white/80 to-green-400 rounded-full animate-pulse shadow-lg shadow-green-400/30"
              style={{ width: "75%" }}
            ></div>
          </div>
        </div> */}

        {/* Contact Information - Highlighted */}
        <div className="space-y-6">
          <div className="text-center">
            <p className="text-xl md:text-2xl text-white/90 mb-4 font-medium">
              Ready to build something amazing together?
            </p>
            <p className="text-lg text-white/70 mb-6">
              Let's discuss your next project
            </p>
          </div>

          {/* Prominent Contact Card */}
          <div className="construction-card p-8 max-w-md mx-auto">
            <div className="text-center space-y-4">
              <div className="w-16 h-16 bg-gradient-to-r from-white/20 to-green-400/40 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg shadow-green-400/20">
                <svg
                  className="w-8 h-8 text-green-300"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                  />
                </svg>
              </div>

              <h3 className="text-xl font-semibold text-white mb-2">
                Get In Touch
              </h3>

              <div className="space-y-3">
                <a
                  href={`mailto:${CONTACT_INFO.email}`}
                  className="block p-4 bg-gradient-to-r from-white/10 to-green-400/20 rounded-lg border border-green-400/40 hover:border-green-300/60 transition-all duration-300 hover:scale-105 group shadow-lg hover:shadow-green-400/20"
                >
                  <div className="text-green-300 group-hover:text-green-200 font-medium text-lg">
                    {CONTACT_INFO.email}
                  </div>
                  <div className="text-white/60 text-sm mt-1">
                    Click to send us an email
                  </div>
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Footer Links */}
        <div className="mt-12 pt-8 border-t border-white/10 text-center">
          <Link
            to="/privacy-policy"
            className="inline-block text-white/60 hover:text-white/90 transition-colors duration-300 text-sm font-medium"
          >
            Privacy Policy
          </Link>
        </div>
        {/* <div className="mt-8 pt-8 border-t border-white/10">
          <p className="text-white/60 text-sm mb-4">
            Connect with ReactiveStack
          </p>
          <div className="flex justify-center space-x-4">
            <a
              href={CONTACT_INFO.social.github}
              onClick={(e) => handleSocialClick(e, "GitHub")}
              className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center hover:bg-green-400/20 transition-colors duration-300 group"
              aria-label="Follow ReactiveStack on GitHub"
            >
              <svg
                className="w-5 h-5 text-white group-hover:text-green-300"
                fill="currentColor"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z" />
              </svg>
            </a>
            <a
              href={CONTACT_INFO.social.twitter}
              onClick={(e) => handleSocialClick(e, "Twitter")}
              className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center hover:bg-green-400/20 transition-colors duration-300 group"
              aria-label="Follow ReactiveStack on Twitter"
            >
              <svg
                className="w-5 h-5 text-white group-hover:text-green-300"
                fill="currentColor"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path d="M24 4.557c-.883.392-1.832.656-2.828.775 1.017-.609 1.798-1.574 2.165-2.724-.951.564-2.005.974-3.127 1.195-.897-.957-2.178-1.555-3.594-1.555-3.179 0-5.515 2.966-4.797 6.045-4.091-.205-7.719-2.165-10.148-5.144-1.29 2.213-.669 5.108 1.523 6.574-.806-.026-1.566-.247-2.229-.616-.054 2.281 1.581 4.415 3.949 4.89-.693.188-1.452.232-2.224.084.626 1.956 2.444 3.379 4.6 3.419-2.07 1.623-4.678 2.348-7.29 2.04 2.179 1.397 4.768 2.212 7.548 2.212 9.142 0 14.307-7.721 13.995-14.646.962-.695 1.797-1.562 2.457-2.549z" />
              </svg>
            </a>
            <a
              href={CONTACT_INFO.social.linkedin}
              onClick={(e) => handleSocialClick(e, "LinkedIn")}
              className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center hover:bg-green-400/20 transition-colors duration-300 group"
              aria-label="Connect with ReactiveStack on LinkedIn"
            >
              <svg
                className="w-5 h-5 text-white group-hover:text-green-300"
                fill="currentColor"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
              </svg>
            </a>
          </div>
        </div> */}
      </div>
    </div>
  );
};

export default UnderConstructionPage;
